import React from "react";

import ContainedTextField from "../../../contained_text_field";
import ContextValueEditor from "./context";
import PromptValueEditor from "./prompt";
import TaskValueEditor from "./task";
import InputSelector from "common_components/flowmaster/input_selector";

import {DataLocation} from "common/flowmaster/types/data_location";
import {
  WorkflowTask,
  ActionDefinition,
  StringArgument,
} from "common/flowmaster/types/task_config";
import {
  Prompt,
  WorkflowContext,
  WorkflowInputs,
} from "common/flowmaster/types/workflow";
import {OrganisationId} from "common/types/organisation";
import AssetEditor from "../../asset";

interface ValueEditorProps {
  argValue: StringArgument;
  priorTasks: WorkflowTask[];
  context: WorkflowContext;
  inputs: WorkflowInputs;
  prompts: Prompt[];
  actionDefinitions: ActionDefinition[];
  onUpdateItem: (value: DataLocation<string>) => void;
  organisationId: OrganisationId;
  handleHasError?: (value: boolean) => void;
  disabled?: boolean;
}

const ValueEditor: React.FC<ValueEditorProps> = ({
  argValue,
  priorTasks,
  context,
  inputs,
  prompts,
  actionDefinitions,
  onUpdateItem,
  organisationId,
  handleHasError,
  disabled,
}) => {
  const stringArg = argValue?.value;
  if (!stringArg || stringArg?.source_type === "none") {
    return null;
  }
  if (stringArg?.source_type === "context") {
    return (
      <ContextValueEditor
        location={stringArg}
        context={context}
        onUpdateItem={onUpdateItem}
        handleHasError={handleHasError}
        disabled={disabled}
      />
    );
  }
  if (stringArg?.source_type === "input") {
    return (
      <InputSelector
        location={stringArg}
        inputs={inputs}
        onUpdateItem={onUpdateItem}
        types={["string", "schema", "llm_model"]}
        handleHasError={handleHasError}
        disabled={disabled}
      />
    );
  }
  if (stringArg?.source_type === "prompt") {
    return (
      <PromptValueEditor
        location={stringArg}
        prompts={prompts}
        onUpdateItem={onUpdateItem}
        organisationId={organisationId}
        handleHasError={handleHasError}
        disabled={disabled}
      />
    );
  }
  if (stringArg?.source_type === "task") {
    return (
      <TaskValueEditor
        location={stringArg}
        priorTasks={priorTasks}
        actionDefinitions={actionDefinitions}
        onUpdateItem={onUpdateItem}
        handleHasError={handleHasError}
        disabled={disabled}
      />
    );
  }
  if (stringArg?.source_type === "variable") {
    return (
      <ContainedTextField
        initialValue={stringArg?.key as string}
        label="Variable name"
        onUpdate={key => onUpdateItem({...stringArg, key})}
      />
    );
  }
  if (
    stringArg?.source_type === "asset" ||
    stringArg?.source_type === "collection_asset"
  ) {
    return (
      <AssetEditor
        location={stringArg}
        onUpdateItem={onUpdateItem}
        priorTasks={priorTasks}
        context={context}
        inputs={inputs}
        prompts={prompts}
        actionDefinitions={actionDefinitions}
        organisationId={organisationId}
        handleHasError={handleHasError}
        disabled={disabled}
      />
    );
  }
  return (
    <ContainedTextField
      initialValue={stringArg?.value as string}
      label="Text"
      multiline
      onUpdate={value => onUpdateItem({...stringArg, value})}
    />
  );
};

export default ValueEditor;
